import { useCallback, useEffect, useId, useState } from 'react';
import type { AdminStats, AdminUser } from '@shared/api';
import { PLANS, type Plan } from '@shared/constants';
import { ConfirmDialog } from '../components/Dialog';
import { Notice, Toast } from '../components/Notice';
import { TestUserDialog } from '../components/TestUserDialog';
import { del, errorMessage, get, patch, post } from '../lib/api';
import { useSession } from '../state/session';
import { PageShell } from './ProfilePage';

const PLAN_LABEL: Record<Plan, string> = { free_trial: 'Período grátis', standard: 'Standard', free: 'Gratuito' };
const STATUS_LABEL = { active: 'ativo', inactive: 'pendente', cancelled: 'cancelado', pending: 'aguardando' } as const;

function date(iso: string | null) {
  return iso ? new Date(iso).toLocaleDateString('pt-BR') : '—';
}

export function AdminPage() {
  const { user } = useSession();
  const searchId = useId();
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [toDelete, setToDelete] = useState<AdminUser | null>(null);
  const [busy, setBusy] = useState(false);
  const [creating, setCreating] = useState(false);
  const [toConvert, setToConvert] = useState<AdminUser | null>(null);

  const load = useCallback(async (q: string) => {
    setError(null);
    try {
      const [s, list] = await Promise.all([
        get<AdminStats>('/api/admin/stats'),
        get<{ users: AdminUser[] }>(`/api/admin/users${q ? `?q=${encodeURIComponent(q)}` : ''}`),
      ]);
      setStats(s);
      setUsers(list.users);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounced search.
  useEffect(() => {
    const id = setTimeout(() => void load(query.trim()), 250);
    return () => clearTimeout(id);
  }, [query, load]);

  async function changePlan(target: AdminUser, plan: Plan) {
    try {
      await patch(`/api/admin/users/${encodeURIComponent(target.id)}/plan`, { plan, paymentStatus: 'active' });
      setToast(`Plano de ${target.email} alterado para ${PLAN_LABEL[plan]}.`);
      await load(query.trim());
    } catch (err) {
      setError(errorMessage(err));
    }
  }

  async function confirmConvert() {
    if (!toConvert) return;
    setBusy(true);
    try {
      const { user: converted } = await post<{ user: AdminUser }>(`/api/admin/users/${encodeURIComponent(toConvert.id)}/convert`);
      setToast(`${converted.email} agora está no plano normal: grátis até ${date(converted.trialEndsAt)}. Envie o link “adicionar cartão”.`);
      setToConvert(null);
      await load(query.trim());
    } catch (err) {
      setError(errorMessage(err));
      setToConvert(null);
    } finally {
      setBusy(false);
    }
  }

  async function confirmDelete() {
    if (!toDelete) return;
    setBusy(true);
    try {
      await del(`/api/admin/users/${encodeURIComponent(toDelete.id)}`);
      setToast(`Conta ${toDelete.email} excluída.`);
      setToDelete(null);
      await load(query.trim());
    } catch (err) {
      setError(errorMessage(err));
      setToDelete(null);
    } finally {
      setBusy(false);
    }
  }

  async function copyResetLink(target: AdminUser) {
    try {
      const { link, validHours } = await post<{ link: string; validHours: number }>(
        `/api/admin/users/${encodeURIComponent(target.id)}/reset-link`,
      );
      const text = `Voz Livre App: para criar uma nova senha, abra este link (vale por ${validHours} horas e só pode ser usado uma vez):\n${link}`;
      try {
        await navigator.clipboard.writeText(text);
        setToast(`Link de nova senha de ${target.email} copiado. Envie para a pessoa.`);
      } catch {
        window.prompt('Copie o link de nova senha:', link);
      }
    } catch (err) {
      setError(errorMessage(err));
    }
  }

  async function copyCardLink() {
    const link = `${window.location.origin}/adicionar-cartao`;
    try {
      await navigator.clipboard.writeText(link);
      setToast('Link para adicionar cartão copiado.');
    } catch {
      window.prompt('Copie o link:', link);
    }
  }

  return (
    <PageShell title="Administração">
      {error && <Notice kind="error">{error}</Notice>}

      {stats && (
        <section className="stats" aria-label="Resumo de usuários">
          <div className="stat">
            <span className="stat__value">{stats.total}</span>
            <span className="stat__label">Usuários</span>
          </div>
          <div className="stat">
            <span className="stat__value">{stats.freeTrial}</span>
            <span className="stat__label">Período grátis</span>
          </div>
          <div className="stat">
            <span className="stat__value">{stats.standard}</span>
            <span className="stat__label">Standard</span>
          </div>
          <div className="stat">
            <span className="stat__value">{stats.free}</span>
            <span className="stat__label">Gratuito</span>
          </div>
          <div className="stat">
            <span className="stat__value">{stats.test}</span>
            <span className="stat__label">Contas de teste</span>
          </div>
        </section>
      )}

      <section className="card">
        <div className="toolbar">
          <div className="field field--grow">
            <label htmlFor={searchId}>Pesquisar por nome ou e-mail</label>
            <input id={searchId} type="search" value={query} onChange={(e) => setQuery(e.target.value)} />
          </div>
          <button type="button" className="btn btn--primary" onClick={() => setCreating(true)}>
            ➕ Usuário de teste
          </button>
          <button type="button" className="btn btn--secondary" onClick={() => void copyCardLink()}>
            🔗 Copiar link “adicionar cartão”
          </button>
        </div>

        {loading ? (
          <p className="muted">Carregando…</p>
        ) : users.length === 0 ? (
          <p className="muted">Nenhum usuário encontrado.</p>
        ) : (
          <ul className="user-list">
            {users.map((u) => (
              <li key={u.id} className="user-row">
                <div className="user-row__info">
                  <strong>{u.name || '(sem nome)'}</strong>
                  {u.isAdmin && <span className="badge badge--admin">admin</span>}
                  {u.isTest && <span className="badge badge--test">teste</span>}
                  <span className="muted">{u.email}</span>
                  <span className="small muted">
                    Criado em {date(u.createdAt)}
                    {u.isTest ? ' · sem cobrança' : ` · pagamento ${STATUS_LABEL[u.paymentStatus]}`}
                    {!u.isTest && u.plan === 'free_trial' && ` · grátis até ${date(u.trialEndsAt)}`}
                    {u.hasSubscription && ' · assinatura Stripe'}
                  </span>
                </div>
                <div className="user-row__actions">
                  {u.isTest ? (
                    <button type="button" className="btn btn--small btn--secondary" onClick={() => setToConvert(u)}>
                      Migrar para plano normal
                    </button>
                  ) : (
                    <>
                      <label className="sr-only" htmlFor={`plan-${u.id}`}>
                        Plano de {u.email}
                      </label>
                      <select id={`plan-${u.id}`} value={u.plan} onChange={(e) => void changePlan(u, e.target.value as Plan)}>
                        {PLANS.map((p) => (
                          <option key={p} value={p}>
                            {PLAN_LABEL[p]}
                          </option>
                        ))}
                      </select>
                    </>
                  )}
                  <button
                    type="button"
                    className="btn btn--small btn--ghost"
                    title="Copia um link para a pessoa criar uma nova senha"
                    onClick={() => void copyResetLink(u)}
                  >
                    🔑 Senha
                  </button>
                  <button
                    type="button"
                    className="btn btn--small btn--danger-ghost"
                    disabled={u.id === user?.id}
                    onClick={() => setToDelete(u)}
                  >
                    Excluir
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <ConfirmDialog
        open={!!toConvert}
        title="Migrar para plano normal"
        message={`${toConvert?.email ?? ''} deixa de ser conta de teste. A partir de hoje começam os 45 dias grátis. Depois disso, com cartão cadastrado, a assinatura de R$ 19,90/mês é cobrada; sem cartão, a conta passa ao plano gratuito (sem voz). Os dados e o quadro da pessoa continuam iguais.`}
        confirmLabel="Migrar"
        busy={busy}
        onConfirm={() => void confirmConvert()}
        onCancel={() => setToConvert(null)}
      />
      <ConfirmDialog
        open={!!toDelete}
        title="Excluir conta"
        message={`Excluir a conta ${toDelete?.email ?? ''} e todos os símbolos e categorias personalizados dela? Se houver assinatura no Stripe, ela será cancelada. Esta ação não pode ser desfeita.`}
        confirmLabel="Excluir definitivamente"
        danger
        busy={busy}
        onConfirm={() => void confirmDelete()}
        onCancel={() => setToDelete(null)}
      />
      <TestUserDialog
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={(created) => {
          setToast(`Usuário de teste ${created.email} criado.`);
          void load(query.trim());
        }}
      />
      <Toast message={toast} onDone={() => setToast(null)} />
    </PageShell>
  );
}
